import { mockState } from '@/api/mock-data'
import { USE_MOCK } from '@/config'
import type { PdAccessLogRow } from '@/types/audit'
import { createClient } from '@/api/supabase'

export interface PdLogListParams {
  page?: number
  pageSize?: number
  userId?: string
  dateFrom?: string
  dateTo?: string
}

export interface PdLogListResult {
  items: PdAccessLogRow[]
  total: number
}

export async function listPdAccessLog(
  params: PdLogListParams,
): Promise<PdLogListResult> {
  if (USE_MOCK) {
    const page = Math.max(1, params.page ?? 1)
    const pageSize = Math.min(100, params.pageSize ?? 25)
    let rows = [...mockState.pdAccessLog]
    if (params.userId) rows = rows.filter((r) => r.user_id === params.userId)
    if (params.dateFrom) {
      const t = new Date(params.dateFrom).getTime()
      rows = rows.filter((r) => new Date(r.created_at).getTime() >= t)
    }
    if (params.dateTo) {
      const t = new Date(params.dateTo).getTime()
      rows = rows.filter((r) => new Date(r.created_at).getTime() <= t)
    }
    rows.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    const total = rows.length
    const start = (page - 1) * pageSize
    return { items: rows.slice(start, start + pageSize), total }
  }

  const supabase = createClient()
  const page = Math.max(1, params.page ?? 1)
  const pageSize = Math.min(100, params.pageSize ?? 25)
  const from = (page - 1) * pageSize
  const to = from + pageSize - 1

  // NB: без PostgREST-embed profiles(full_name) — FK pd_access_log.accessor_id
  // ссылается на auth.users, а не на public.profiles, embed падает PGRST200.
  // Двухшаговый fetch: сначала строки, затем имена по accessor_id.
  let query = supabase
    .from('pd_access_log')
    .select('id, accessed_at, accessor_id, order_id', { count: 'exact' })
    .order('accessed_at', { ascending: false })

  if (params.userId) query = query.eq('accessor_id', params.userId)
  if (params.dateFrom) query = query.gte('accessed_at', params.dateFrom)
  if (params.dateTo) query = query.lte('accessed_at', params.dateTo)

  const { data, error, count } = await query.range(from, to)
  if (error) throw new Error(error.message)

  const rows = (data ?? []) as Array<{
    id: string
    accessed_at: string
    accessor_id: string | null
    order_id: string
  }>

  const actorIds = Array.from(
    new Set(rows.map((r) => r.accessor_id).filter((v): v is string => !!v)),
  )
  let nameById = new Map<string, string>()
  if (actorIds.length > 0) {
    const { data: profs, error: profErr } = await supabase
      .from('profiles')
      .select('id, full_name')
      .in('id', actorIds)
    if (profErr) throw new Error(profErr.message)
    nameById = new Map(
      (profs ?? []).map((p) => [p.id as string, (p.full_name as string | null) ?? '']),
    )
  }

  const items: PdAccessLogRow[] = rows.map((row) => ({
    id: row.id,
    created_at: row.accessed_at,
    user_id: row.accessor_id ?? '',
    user_name: row.accessor_id ? nameById.get(row.accessor_id) ?? null : null,
    order_id: row.order_id,
  }))

  return { items, total: count ?? 0 }
}
